import { ChainId } from '@src/configs/chainInfo'
import { evmAddressToBytes32, evmBytes32ToAddress, isValidEvmBytes32Address } from '@src/utils/evm/addressBytes32'
import { createContextLogger } from '@src/utils/logger'
import {
    isValidSolanaBytes32Address,
    solanaAddressToBytes32,
    solanaBytes32ToAddress,
} from '@src/utils/solana/addressBytes32'
import { isValidTronBytes32Address, tronAddressToBytes32, tronBytes32ToAddress } from '@src/utils/tron/addressBytes32'

const logger = createContextLogger('AddressBytes32')

/**
 * Converts an address (EVM, Solana, or Tron) to bytes32 format
 */
export const addressToBytes32 = (address: string, chainId: number): `0x${string}` | string => {
    logger.info('addressToBytes32', address, chainId)

    try {
        if (chainId === ChainId.Solana) {
            return solanaAddressToBytes32(address)
        } else if (chainId === ChainId.Tron) {
            logger.info('address TRON', address)
            return tronAddressToBytes32(address)
        } else {
            return evmAddressToBytes32(address)
        }
    } catch (err) {
        logger.error('Error converting address to bytes32:', err)
        return address
    }
}

/**
 * Converts bytes32 back to an address format
 */
export const bytes32ToAddress = (bytes32: `0x${string}`, chainId: number): string => {
    try {
        if (chainId === ChainId.Solana) {
            return solanaBytes32ToAddress(bytes32)
        } else if (chainId === ChainId.Tron) {
            return tronBytes32ToAddress(bytes32)
        } else {
            return evmBytes32ToAddress(bytes32)
        }
    } catch (err) {
        logger.error('Error converting bytes32 to address:', err)
        throw new Error(`Failed to convert bytes32 ${bytes32} to address`)
    }
}

/**
 * Helper function to convert address to raw bytes (Buffer)
 */
export const addressToBytes = (address: string, chainId: number): Buffer => {
    const bytes32 = addressToBytes32(address, chainId)
    return Buffer.from(bytes32.slice(2), 'hex')
}

/**
 * Helper function to convert raw bytes (Buffer) to address
 */
export const bytesToAddress = (addressBytes: Buffer, chainId: number): string => {
    const bytes32 = `0x${addressBytes.toString('hex')}`
    return bytes32ToAddress(bytes32 as `0x${string}`, chainId)
}

/**
 * Validates if a bytes32 value represents a valid address for the given chain
 */
export const isValidBytes32Address = (bytes32: string, chainId: number): boolean => {
    if (chainId === ChainId.Solana) {
        return isValidSolanaBytes32Address(bytes32)
    } else if (chainId === ChainId.Tron) {
        return isValidTronBytes32Address(bytes32)
    } else {
        return isValidEvmBytes32Address(bytes32)
    }
}

export {
    // EVM exports
    evmAddressToBytes32,
    evmBytes32ToAddress,
    isValidEvmBytes32Address,
    // Solana exports
    solanaAddressToBytes32,
    solanaBytes32ToAddress,
    isValidSolanaBytes32Address,
    // Tron exports
    tronAddressToBytes32,
    tronBytes32ToAddress,
    isValidTronBytes32Address,
}
